import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../services/api';
import {
  Calculator,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Loader2,
  Hash,
  BarChart3,
  Sigma,
  Divide,
  X,
  Info
} from 'lucide-react';

const SCORING_TYPES = [
  { value: 'summed', label: 'Summed Score', icon: Sigma, description: 'Add up all response values' },
  { value: 'mean', label: 'Mean (Total Items)', icon: Divide, description: 'Sum ÷ total items defined' },
  { value: 'mean_answered', label: 'Mean (Answered)', icon: Divide, description: 'Sum ÷ items answered' },
  { value: 'irt', label: 'IRT-Based', icon: BarChart3, description: 'Item Response Theory scoring' }
];

const ScoringTab = ({ assessment }) => {
  const [scoringRules, setScoringRules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Use uuid for API URL paths
  const assessmentId = assessment?.uuid || assessment?.assessment_uuid;
  const surveyId = assessment?.survey?.id || assessment?.survey_id;

  useEffect(() => {
    if (assessmentId) {
      fetchScoringRules();
      fetchQuestions();
    }
  }, [assessmentId]);

  const fetchScoringRules = async () => {
    try {
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/scoring-rules/`);
      // API returns { scoring_rules: [...] }
      const rules = response.data?.scoring_rules || response.data?.results || [];
      setScoringRules(Array.isArray(rules) ? rules : []);
    } catch (error) {
      console.error('Error fetching scoring rules:', error);
      toast.error('Failed to load scoring rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      // Try assessment questions endpoint first (includes items from canvas)
      const response = await api.get(`/api/assessment-builder/assessments/${assessmentId}/questions/`);
      const questionsData = response.data?.questions || response.data?.results || response.data || [];
      
      if (Array.isArray(questionsData) && questionsData.length > 0) {
        setQuestions(questionsData);
      } else if (surveyId) {
        // Fallback to survey questions endpoint
        const surveyResponse = await api.get(`/api/surveys/${surveyId}/questions/`);
        const surveyQuestions = surveyResponse.data?.results || surveyResponse.data || [];
        setQuestions(Array.isArray(surveyQuestions) ? surveyQuestions : []);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      // Don't toast - questions are optional for viewing rules
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleDeleteRule = async (rule) => {
    if (!confirm(`Delete scoring rule "${rule.name}"? This cannot be undone.`)) return;
    
    setDeleting(rule.id);
    try {
      await api.delete(`/api/assessment-builder/scoring-rules/${rule.id}/`);
      setScoringRules(prev => prev.filter(r => r.id !== rule.id));
      toast.success('Scoring rule deleted');
    } catch (error) {
      console.error('Error deleting rule:', error);
      toast.error('Failed to delete scoring rule');
    } finally {
      setDeleting(null);
    }
  };

  const handleSaveRule = async (formData) => {
    try {
      if (editingRule) {
        const response = await api.patch(`/api/assessment-builder/scoring-rules/${editingRule.id}/`, formData);
        setScoringRules(prev => prev.map(r => r.id === editingRule.id ? response.data : r));
        toast.success('Scoring rule updated');
      } else {
        const response = await api.post(`/api/assessment-builder/assessments/${assessmentId}/scoring-rules/`, formData);
        setScoringRules(prev => [...prev, response.data]);
        toast.success('Scoring rule created');
      }
      setShowModal(false);
      setEditingRule(null);
    } catch (error) {
      console.error('Error saving rule:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.code?.[0] || 'Failed to save scoring rule';
      toast.error(errorMsg);
      throw error;
    }
  };

  const getTypeInfo = (type) => SCORING_TYPES.find(t => t.value === type) || SCORING_TYPES[0];

  const handleToggleActive = async (rule) => {
    try {
      const response = await api.patch(`/api/assessment-builder/scoring-rules/${rule.id}/`, {
        is_active: !rule.is_active
      });
      setScoringRules(prev => prev.map(r => r.id === rule.id ? response.data : r));
      toast.success(response.data.is_active ? 'Scoring rule activated' : 'Scoring rule deactivated');
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Failed to update scoring rule');
    }
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center py-12"
      >
        <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
        <span className="ml-2 text-gray-600">Loading scoring rules...</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary-600" />
            Scoring Rules
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Define how scores are calculated when participants complete this assessment
          </p>
        </div>
        <button
          onClick={handleAddRule}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Rule
        </button>
      </div>

      {/* Rules List */}
      {scoringRules.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-8 text-center border-2 border-dashed border-gray-200 dark:border-gray-700">
          <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-gray-700 font-medium mb-1">No scoring rules defined</h4>
          <p className="text-gray-500 text-sm mb-4">
            Add scoring rules to automatically calculate scores when participants complete this assessment.
          </p>
          <button
            onClick={handleAddRule}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Add Scoring Rule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {scoringRules.map((rule) => {
            const typeInfo = getTypeInfo(rule.scoring_type);
            const TypeIcon = typeInfo.icon;
            const itemCount = rule.included_questions?.length || 0;
            
            return (
              <div
                key={rule.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-200 hover:shadow-sm dark:shadow-gray-900/20 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${rule.is_active ? 'bg-primary-100' : 'bg-gray-100 dark:bg-gray-800'}`}>
                      <TypeIcon className={`w-5 h-5 ${rule.is_active ? 'text-primary-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{rule.name}</h4>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Hash className="w-3.5 h-3.5" />
                          {rule.code}
                        </span>
                        <span>•</span>
                        <span>{typeInfo.label}</span>
                        <span>•</span>
                        <span>{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                      </div>
                      {rule.description && (
                        <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
                      )}
                      {rule.interpretation_ranges?.length > 0 && (
                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                          {rule.interpretation_ranges.map((range, idx) => (
                            <span
                              key={idx}
                              className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 rounded"
                            >
                              {range.min}-{range.max}: {range.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(rule)}
                      className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                        rule.is_active ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                      title={rule.is_active ? 'Click to deactivate' : 'Click to activate'}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          rule.is_active ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => handleEditRule(rule)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Edit rule"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule)}
                      disabled={deleting === rule.id}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete rule"
                    >
                      {deleting === rule.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">How scoring works</p>
          <p className="mt-1 text-blue-700">
            Scores are automatically calculated when participants complete surveys using this assessment. 
            View calculated scores in the Data Export section or via the Participant Scores API.
          </p>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <ScoringRuleModal
            rule={editingRule}
            questions={questions}
            onSave={handleSaveRule}
            onClose={() => {
              setShowModal(false);
              setEditingRule(null);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ScoringRuleModal = ({ rule, questions, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    code: rule?.code || '',
    description: rule?.description || '',
    scoring_type: rule?.scoring_type || 'summed',
    included_questions: rule?.included_questions || [],
    item_weights: rule?.item_weights || {},
    rescale_multiplier: rule?.rescale_multiplier || '',
    rescale_offset: rule?.rescale_offset || '',
    interpretation_ranges: rule?.interpretation_ranges || [],
    is_active: rule?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(
    !!(rule?.rescale_multiplier || rule?.rescale_offset || rule?.interpretation_ranges?.length || (rule?.item_weights && Object.keys(rule.item_weights).length > 0))
  );
  const [useCustomWeights, setUseCustomWeights] = useState(
    !!(rule?.item_weights && Object.keys(rule.item_weights).length > 0)
  );
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (!/^[A-Z0-9_]+$/.test(formData.code)) newErrors.code = 'Code must be uppercase letters, numbers, and underscores only';
    if (formData.included_questions.length === 0) newErrors.questions = 'Select at least one question';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      // Convert item_weights string values to numbers
      const cleanedWeights = {};
      for (const [qId, weights] of Object.entries(formData.item_weights)) {
        cleanedWeights[qId] = {};
        for (const [respVal, weight] of Object.entries(weights)) {
          cleanedWeights[qId][respVal] = weight === '' ? Number(respVal) : Number(weight);
        }
      }
      
      const payload = {
        ...formData,
        item_weights: cleanedWeights,
        rescale_multiplier: formData.rescale_multiplier || null,
        rescale_offset: formData.rescale_offset || null
      };
      await onSave(payload);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };

  const toggleQuestion = (questionId) => {
    setFormData(prev => ({
      ...prev,
      included_questions: prev.included_questions.includes(questionId)
        ? prev.included_questions.filter(id => id !== questionId)
        : [...prev.included_questions, questionId]
    }));
  };

  const selectAllQuestions = () => {
    setFormData(prev => ({
      ...prev,
      included_questions: questions.map(q => q.id)
    }));
  };

  const deselectAllQuestions = () => {
    setFormData(prev => ({
      ...prev,
      included_questions: []
    }));
  };

  const addInterpretationRange = () => {
    setFormData(prev => ({
      ...prev,
      interpretation_ranges: [
        ...prev.interpretation_ranges,
        { min: 0, max: 0, label: '' }
      ]
    }));
  };

  const updateInterpretationRange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      interpretation_ranges: prev.interpretation_ranges.map((range, i) =>
        i === index ? { ...range, [field]: field === 'label' ? value : Number(value) } : range
      )
    }));
  };

  const removeInterpretationRange = (index) => {
    setFormData(prev => ({
      ...prev,
      interpretation_ranges: prev.interpretation_ranges.filter((_, i) => i !== index)
    }));
  };

  const getQuestionResponseOptions = (question) => {
    // Get response options from question - support various formats
    const options = question.response_options || question.responseOptions || question.options || [];
    if (Array.isArray(options)) return options;
    return [];
  };

  const updateItemWeight = (questionId, responseValue, weight) => {
    setFormData(prev => {
      const newWeights = { ...prev.item_weights };
      if (!newWeights[questionId]) {
        newWeights[questionId] = {};
      }
      // Store raw value during editing to allow backspace/clearing
      // Will be converted to number when form is submitted
      newWeights[questionId][String(responseValue)] = weight === '' ? '' : weight;
      return { ...prev, item_weights: newWeights };
    });
  };

  const resetQuestionWeights = (questionId) => {
    setFormData(prev => {
      const newWeights = { ...prev.item_weights };
      delete newWeights[questionId];
      return { ...prev, item_weights: newWeights };
    });
  };

  const toggleCustomWeights = (enabled) => {
    setUseCustomWeights(enabled);
    if (!enabled) {
      setFormData(prev => ({ ...prev, item_weights: {} }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-5 h-5 text-white" />
            <h3 className="text-lg font-semibold text-white">
              {rule ? 'Edit Scoring Rule' : 'Add Scoring Rule'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Name & Code */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., Total Score"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono ${
                  errors.code ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., TOTAL"
              />
              {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
              <p className="text-gray-500 text-xs mt-1">Short code for exports</p>
            </div>
          </div>

          {/* Scoring Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Scoring Type</label>
            <div className="grid grid-cols-2 gap-2">
              {SCORING_TYPES.map((type) => {
                const TypeIcon = type.icon;
                const isSelected = formData.scoring_type === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, scoring_type: type.value }))}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <TypeIcon className={`w-4 h-4 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`} />
                      <span className={`font-medium text-sm ${isSelected ? 'text-primary-700' : 'text-gray-700'}`}>
                        {type.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
              placeholder="Optional description of this scoring rule"
            />
          </div>

          {/* Questions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Questions to Include <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllQuestions}
                  className="text-xs text-primary-600 hover:text-primary-700"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  onClick={deselectAllQuestions}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>
            {errors.questions && <p className="text-red-500 text-xs mb-2">{errors.questions}</p>}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
              {questions.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No questions found in this assessment
                </div>
              ) : (
                questions.map((question, index) => {
                  const isSelected = formData.included_questions.includes(question.id);
                  return (
                    <label
                      key={question.id}
                      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b last:border-b-0 ${
                        isSelected ? 'bg-primary-50' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleQuestion(question.id)}
                        className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-500">Q{index + 1}:</span>{' '}
                        <span className="text-sm text-gray-900 line-clamp-2">
                          {question.question_text || question.text || 'Untitled question'}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formData.included_questions.length} of {questions.length} selected
            </p>
          </div>

          {/* Advanced Options */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Advanced Options
            </button>
            
            {showAdvanced && (
              <div className="mt-4 space-y-4 pl-6 border-l-2 border-gray-100 dark:border-gray-700">
                {/* Rescaling */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rescaling (optional)</label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Multiplier</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.rescale_multiplier}
                        onChange={(e) => setFormData(prev => ({ ...prev, rescale_multiplier: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Offset</label>
                      <input
                        type="number"
                        step="any"
                        value={formData.rescale_offset}
                        onChange={(e) => setFormData(prev => ({ ...prev, rescale_offset: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                        placeholder="e.g., 50"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Formula: rescaled = (raw × multiplier) + offset
                  </p>
                </div>

                {/* Interpretation Ranges */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Interpretation Ranges</label>
                    <button
                      type="button"
                      onClick={addInterpretationRange}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add Range
                    </button>
                  </div>
                  {formData.interpretation_ranges.length === 0 ? (
                    <p className="text-sm text-gray-500">No interpretation ranges defined</p>
                  ) : (
                    <div className="space-y-2">
                      {formData.interpretation_ranges.map((range, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="number"
                            value={range.min}
                            onChange={(e) => updateInterpretationRange(index, 'min', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Min"
                          />
                          <span className="text-gray-400">-</span>
                          <input
                            type="number"
                            value={range.max}
                            onChange={(e) => updateInterpretationRange(index, 'max', e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Max"
                          />
                          <input
                            type="text"
                            value={range.label}
                            onChange={(e) => updateInterpretationRange(index, 'label', e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Label (e.g., Minimal)"
                          />
                          <button
                            type="button"
                            onClick={() => removeInterpretationRange(index)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Custom Item Weights */}
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      type="button"
                      onClick={() => toggleCustomWeights(!useCustomWeights)}
                      className={`relative w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                        useCustomWeights ? 'bg-primary-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          useCustomWeights ? 'translate-x-4' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <label className="text-sm font-medium text-gray-700">Use custom item weights</label>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    Override default response values with custom scoring weights
                  </p>
                  
                  {useCustomWeights && formData.included_questions.length > 0 && (() => {
                    // Filter to only questions with discrete response options (exclude VAS/slider)
                    const questionsWithOptions = formData.included_questions
                      .map(qId => {
                        const question = questions.find(q => q.id === qId);
                        if (!question) return null;
                        const responseOptions = getQuestionResponseOptions(question);
                        return responseOptions.length > 0 ? { qId, question, responseOptions } : null;
                      })
                      .filter(Boolean);
                    
                    if (questionsWithOptions.length === 0) {
                      return (
                        <p className="text-sm text-gray-500 italic">
                          No questions with discrete response options selected. VAS/slider questions use rescaling instead.
                        </p>
                      );
                    }
                    
                    return (
                      <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                        {questionsWithOptions.map(({ qId, question, responseOptions }) => {
                          const questionWeights = formData.item_weights[qId] || {};
                          const hasCustomWeights = Object.keys(questionWeights).length > 0;
                          
                          return (
                            <div key={qId} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
                                  Q{question.order || questions.indexOf(question) + 1}: {(question.text || question.question_text || '').substring(0, 40)}...
                                </span>
                                {hasCustomWeights && (
                                  <button
                                    type="button"
                                    onClick={() => resetQuestionWeights(qId)}
                                    className="text-xs text-gray-500 hover:text-red-500"
                                  >
                                    Reset
                                  </button>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {responseOptions.map((opt, idx) => {
                                  const optValue = opt.value ?? opt.numeric_value ?? idx;
                                  const currentWeight = questionWeights[String(optValue)] ?? optValue;
                                  return (
                                    <div key={idx} className="flex items-center gap-1 text-xs">
                                      <span className="text-gray-500 w-6 text-right">{optValue}→</span>
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9\-\.]*"
                                        value={currentWeight}
                                        onChange={(e) => updateItemWeight(qId, optValue, e.target.value)}
                                        className="w-12 px-1.5 py-1 border border-gray-300 rounded text-center text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {useCustomWeights && formData.included_questions.length === 0 && (
                    <p className="text-sm text-gray-500 italic">Select questions above to configure weights</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div>
              <span className="font-medium text-gray-900">Active</span>
              <p className="text-sm text-gray-500">Calculate this score on survey completion</p>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
              className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                formData.is_active ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  formData.is_active ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {rule ? 'Save Changes' : 'Create Rule'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ScoringTab;
